import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { palettePrimaryMain, palettePrimaryMutedSurface } from '@/theme/designTokens'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { ConfirmCancelModal } from '@/shared/components/ConfirmCancelModal'
import { ConfirmPositiveModal } from '@/shared/components/confirmation'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  Navigate,
  useBlocker,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast/ToastProvider'
import { useSessionStore } from '@/stores/useSessionStore'
import { ApiError } from '@/api/http/types'
import { isAbortError, isNetworkApiError } from '@/shared/lib/apiErrorMessages'
import { checkWizardAiReadiness } from '../api/campaign-wizard-readiness'
import { messageFromCampaignAiStepError } from '../lib/mapApiUnknownError'
import { useCampaign } from '../api/campaign-queries'
import { useCompleteStep2, usePatchCampaign } from '../api/campaign-mutations'
import { CampaignWizardChrome } from '../components/CampaignWizardChrome'
import { CampaignGeneratingDialog } from '../components/CampaignGeneratingDialog'
import researchGoalEditIconUrl from '@/assets/information-2-line 2.svg'
import clarifyingQuestionsSectionIconUrl from '@/assets/Component 1.svg'
import clarifyingQuestionsInfoIconUrl from '@/assets/information-2-line 1.svg'
import clarifyingInfoCloseIconUrl from '@/assets/close-circle-line 1.svg'
import type {
  ClarifyingQuestionItem,
  ResearchGoalInterpretation,
  SmartFacet,
} from '../types'
import { smartFacetToParagraph } from '../lib/smart-facet-display'

/** One interpreted block: section title + single body that weaves all SMART dimensions together. */
function SmartFacetSection({
  title,
  facet,
  testIdSuffix,
}: Readonly<{ title: string; facet: SmartFacet; testIdSuffix: string }>) {
  return (
    <Box data-testid={`smart-block-${testIdSuffix}`} sx={{ mb: 0 }}>
      <Typography
        component="h3"
        sx={{
          fontSize: 14,
          fontWeight: 600,
          lineHeight: '20px',
          color: '#0f172a',
          letterSpacing: 0,
          mb: 2,
        }}
      >
        {title}:
      </Typography>
      <Typography
        data-testid={`smart-block-${testIdSuffix}-description`}
        sx={{
          fontSize: 14,
          fontWeight: 400,
          lineHeight: '20px',
          color: '#64748b',
        }}
      >
        {smartFacetToParagraph(facet)}
      </Typography>
    </Box>
  )
}

function SecondaryObjectivesSection({
  facets,
}: Readonly<{ facets: SmartFacet[] }>) {
  if (facets.length === 1) {
    return (
      <SmartFacetSection
        title="Secondary objectives"
        facet={facets[0]}
        testIdSuffix="secondary-0"
      />
    )
  }
  return (
    <Box>
      <Typography
        component="h3"
        sx={{
          fontSize: 14,
          fontWeight: 600,
          lineHeight: '20px',
          color: '#0f172a',
          mb: 2,
        }}
      >
        Secondary objectives:
      </Typography>
      <Stack spacing={2}>
        {facets.map((f, i) => (
          <SmartFacetSection
            key={[i, f.specific, f.measurable, f.achievable, f.relevant, f.time_bound].join('|')}
            title={`Objective ${i + 1}`}
            facet={f}
            testIdSuffix={`secondary-${i}`}
          />
        ))}
      </Stack>
    </Box>
  )
}

export function CampaignGoalAnalysisPage() {
  const role = useSessionStore((s) => s.user?.role)
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const navigate = useNavigate()
  useDocumentTitle('AI Goal Analysis')

  const { data: campaign, isLoading, isError, refetch } = useCampaign(campaignId)
  const patchCampaign = usePatchCampaign()
  const completeStep2 = useCompleteStep2()
  const { showToast } = useToast()

  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [backOpen, setBackOpen] = useState(false)
  const [editGoalOpen, setEditGoalOpen] = useState(false)
  const [loiOpen, setLoiOpen] = useState(false)
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(null)
  const leaveRef = useRef(false)
  const loiGenerationFailureHandledRef = useRef(false)
  const loiAnalyzeAbortRef = useRef<AbortController | null>(null)

  const interpretation = campaign?.researchGoalInterpretation as
    | ResearchGoalInterpretation
    | null
    | undefined

  const questions = useMemo((): ClarifyingQuestionItem[] => {
    const raw = campaign?.clarifyingQuestions as
      | { questions?: ClarifyingQuestionItem[] }
      | null
      | undefined
    return Array.isArray(raw?.questions) ? raw.questions : []
  }, [campaign?.clarifyingQuestions])

  useEffect(() => {
    if (campaign?.clarifyingAnswers) {
      setAnswers({ ...campaign.clarifyingAnswers })
    }
  }, [campaign?.clarifyingAnswers, campaign?.id])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !leaveRef.current &&
      currentLocation.pathname.endsWith('/goal-analysis') &&
      nextLocation.pathname.endsWith('/setup'),
  )

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setBackOpen(true)
    }
  }, [blocker.state])

  const openBackModal = useCallback(() => {
    setBackOpen(true)
  }, [])

  const openEditGoalModal = useCallback(() => {
    setEditGoalOpen(true)
  }, [])

  const cancelEditGoal = useCallback(() => {
    setEditGoalOpen(false)
  }, [])

  const confirmGoBack = useCallback(() => {
    setBackOpen(false)
    setAnswers({})
    leaveRef.current = true
    if (blocker.state === 'blocked') {
      blocker.proceed()
    } else {
      navigate(`/campaigns/${campaignId}/setup`)
    }
    queueMicrotask(() => {
      leaveRef.current = false
    })
  }, [blocker, campaignId, navigate])

  const cancelBack = useCallback(() => {
    setBackOpen(false)
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }, [blocker])

  const confirmEditGoal = useCallback(() => {
    setEditGoalOpen(false)
    setAnswers({})
    leaveRef.current = true
    if (blocker.state === 'blocked') {
      blocker.proceed()
    } else {
      navigate(`/campaigns/${campaignId}/setup`)
    }
    queueMicrotask(() => {
      leaveRef.current = false
    })
  }, [blocker, campaignId, navigate])

  const onSelectPill = useCallback(
    async (questionId: string, option: string) => {
      const next = { ...answers, [questionId]: option }
      setAnswers(next)
      try {
        await patchCampaign.mutateAsync({
          campaignId,
          body: { clarifyingAnswers: next },
        })
      } catch {
        /* toast optional */
      }
    },
    [answers, campaignId, patchCampaign],
  )

  const failLoiGeneration = useCallback(
    (e?: unknown) => {
      if (loiGenerationFailureHandledRef.current) return
      loiGenerationFailureHandledRef.current = true
      loiAnalyzeAbortRef.current?.abort()
      loiAnalyzeAbortRef.current = null
      setLoiOpen(false)
      showToast(messageFromCampaignAiStepError(e), 'error')
    },
    [showToast],
  )

  const onProposeLoi = useCallback(async () => {
    loiGenerationFailureHandledRef.current = false
    loiAnalyzeAbortRef.current?.abort()
    const controller = new AbortController()
    loiAnalyzeAbortRef.current = controller
    setLoiOpen(true)
    try {
      await checkWizardAiReadiness(controller.signal)
      await completeStep2.mutateAsync({
        campaignId,
        body: { clarifyingAnswers: answers },
        signal: controller.signal,
      })
      loiAnalyzeAbortRef.current = null
      setLoiOpen(false)
      // Push so Browser Back from LOI returns to AI Goal Analysis (US187).
      navigate(`/campaigns/${campaignId}/lines-of-inquiry`)
    } catch (e) {
      if (loiGenerationFailureHandledRef.current || isAbortError(e)) return
      setLoiOpen(false)
      if (import.meta.env.DEV) {
        console.error('[campaign-wizard] step 2 LOI analyze failed', e)
      }
      if (e instanceof ApiError) {
        if (
          e.code === 'service_unavailable' ||
          e.code === 'network_error' ||
          e.code === 'unauthorized'
        ) {
          showToast(messageFromCampaignAiStepError(e), 'error')
          return
        }
        showToast(
          e.message || messageFromCampaignAiStepError(e),
          'error',
        )
        return
      }
      if (isNetworkApiError(e)) {
        failLoiGeneration(e)
        return
      }
      failLoiGeneration(e)
    }
  }, [answers, campaignId, completeStep2, failLoiGeneration, navigate])

  useEffect(() => {
    if (!loiOpen) return
    const onOffline = () => {
      failLoiGeneration(
        new Error(
          import.meta.env.DEV
            ? 'Browser went offline (check DevTools → Network → Offline is unchecked)'
            : 'Network connection lost during analysis',
        ),
      )
    }
    globalThis.addEventListener('offline', onOffline)
    return () => globalThis.removeEventListener('offline', onOffline)
  }, [failLoiGeneration, loiOpen])

  if (role !== 'ADMIN') {
    return <Navigate to="/unauthorized" replace />
  }

  if (isLoading) {
    return (
      <CampaignWizardChrome
        wizardStep={2}
        totalWizardSteps={4}
        activePhaseIndex={1}
        campaignId={campaignId}
      >
        <Stack spacing={2} data-testid="goal-analysis-skeleton">
          <Skeleton variant="rounded" height={160} />
          <Skeleton variant="rounded" height={120} />
        </Stack>
      </CampaignWizardChrome>
    )
  }

  if (isError || !campaign) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error" gutterBottom>
          Could not load campaign.
        </Typography>
        <Button onClick={() => refetch()}>Retry</Button>
      </Box>
    )
  }

  if (!interpretation?.primary_objective) {
    return <Navigate to={`/campaigns/${campaignId}/setup`} replace />
  }

  return (
    <>
      <CampaignWizardChrome
        wizardStep={2}
        totalWizardSteps={4}
        activePhaseIndex={1}
        campaignId={campaignId}
      >
        <Stack spacing={3}>
          <Box data-testid="goal-analysis-header">
            <Typography
              variant="h5"
              fontWeight={700}
              color="#23262f"
              letterSpacing="-0.45px"
              lineHeight={1.4}
            >
              AI Goal Analysis
            </Typography>
            <Typography variant="body2" color="#4a5565" sx={{ mt: 0.5 }} letterSpacing="-0.15px">
              Review and edit AI-analyzed research goals.
            </Typography>
          </Box>

          <Card
            variant="outlined"
            data-testid="ai-summary-card"
            sx={{
              borderColor: '#c5d2dc',
              bgcolor: '#f1f8f4',
              borderRadius: '10px',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 3, width: '100%' }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#bdddcb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: 20, color: '#3f8367' }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 600,
                      lineHeight: '28px',
                      color: '#3f8367',
                    }}
                  >
                    AI Research Assistant
                  </Typography>
                </Stack>
                <IconButton
                  size="small"
                  onClick={openEditGoalModal}
                  aria-label="Edit research goal"
                  data-testid="research-goal-edit"
                  sx={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    p: 0,
                    color: palettePrimaryMain,
                    bgcolor: 'transparent',
                    '&:hover': { bgcolor: 'rgba(0, 140, 208, 0.08)' },
                  }}
                >
                  <Box
                    component="img"
                    src={researchGoalEditIconUrl}
                    alt=""
                    aria-hidden
                    sx={{ width: 20, height: 20, display: 'block' }}
                  />
                </IconButton>
              </Stack>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: '#4a5565',
                  mb: 3,
                }}
              >
                I&apos;ve analyzed your research goal for{' '}
                <Box component="span" sx={{ fontWeight: 500, color: '#04183f' }}>
                  {campaign.name?.trim() || 'your campaign'}
                </Box>
                . Here&apos;s my understanding:
              </Typography>
              <Stack spacing={2}>
                <SmartFacetSection
                  title="Primary objective"
                  facet={interpretation.primary_objective}
                  testIdSuffix="primary-objective"
                />
                {interpretation.secondary_objectives?.length ? (
                  <SecondaryObjectivesSection facets={interpretation.secondary_objectives} />
                ) : null}
                <SmartFacetSection
                  title="Target Audience"
                  facet={interpretation.target_consumers}
                  testIdSuffix="target-audience"
                />
              </Stack>
              <Box
                sx={{
                  borderTop: '1px solid #e1e7ef',
                  mt: 3,
                  pt: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: '#64748b',
                  }}
                  data-testid="recommended-loi-count"
                >
                  I&apos;d recommend structuring this as{' '}
                  <Box component="span" sx={{ fontWeight: 500, color: '#0f172a' }}>
                    {interpretation.recommended_loi_count} Lines of Inquiry
                  </Box>{' '}
                  to cover your objectives comprehensively.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {questions.length > 0 ? (
            <Card
              variant="outlined"
              data-testid="clarifying-questions-card"
              sx={{
                borderColor: palettePrimaryMutedSurface,
                bgcolor: '#f1faff',
                borderRadius: '10px',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Header row: icon + title + info icon right-aligned */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#dbeafe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={clarifyingQuestionsSectionIconUrl}
                      alt=""
                      aria-hidden
                      sx={{ width: 20, height: 20, display: 'block' }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 600,
                      lineHeight: '28px',
                      color: palettePrimaryMain,
                    }}
                  >
                    Clarification Points Before Generating LOI
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="About clarification points before generating LOI"
                    data-testid="clarifying-info-icon"
                    onClick={(e) => setInfoAnchorEl(e.currentTarget)}
                    sx={{ ml: 'auto !important' }}
                  >
                    <Box
                      component="img"
                      src={clarifyingQuestionsInfoIconUrl}
                      alt=""
                      aria-hidden
                      sx={{
                        width: '24px',
                        height: '24px',
                        display: 'block',
                      }}
                    />
                  </IconButton>
                </Stack>

                {/* Info card popover — white card with shadow, blue centered text, X close */}
                <Popover
                  open={Boolean(infoAnchorEl)}
                  anchorEl={infoAnchorEl}
                  onClose={() => setInfoAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{
                    paper: {
                      sx: {
                        width: 448,
                        borderRadius: '10px',
                        boxShadow:
                          '0px 20px 25px rgba(0,0,0,0.1), 0px 8px 10px rgba(0,0,0,0.1)',
                        p: 0,
                      },
                    },
                  }}
                  data-testid="clarifying-info-popover"
                >
                  <Box sx={{ px: 3, pt: 1.5, pb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => setInfoAnchorEl(null)}
                        aria-label="Close info"
                        data-testid="clarifying-info-close"
                      >
                        <Box
                          component="img"
                          src={clarifyingInfoCloseIconUrl}
                          alt=""
                          aria-hidden
                          sx={{
                            width: '24px',
                            height: '24px',
                            display: 'block',
                          }}
                        />
                      </IconButton>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: '22.75px',
                        color: '#3b82f6',
                        textAlign: 'center',
                        letterSpacing: '-0.15px',
                      }}
                    >
                      We reviewed your research goal and there are a few clarification questions
                      that need to be clarified.
                    </Typography>
                  </Box>
                </Popover>

                <Stack spacing={4}>
                  {questions.map((q) => (
                    <Box key={q.id} data-testid={`clarifying-question-${q.id}`}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: '20px',
                          color: '#0f1729',
                          letterSpacing: '-0.15px',
                          mb: 1.5,
                        }}
                      >
                        {q.text}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {q.options.map((opt) => {
                          const selected = answers[q.id] === opt
                          return (
                            <Button
                              key={`${q.id}-${opt}`}
                              onClick={() => onSelectPill(q.id, opt)}
                              data-testid={`pill-${q.id}-${opt.replaceAll(/\s+/g, '-')}`}
                              disableElevation
                              sx={{
                                borderRadius: '9999px',
                                px: '13px',
                                py: '7px',
                                fontSize: 14,
                                fontWeight: 400,
                                lineHeight: '20px',
                                textTransform: 'none',
                                border: '1px solid',
                                minWidth: 0,
                                bgcolor: selected ? '#36b7fa' : 'white',
                                borderColor: selected ? '#36b7fa' : '#e1e7ef',
                                color: selected ? 'white' : '#0f1729',
                                '&:hover': {
                                  bgcolor: selected ? '#1fa8f0' : '#f8fafc',
                                  borderColor: selected ? '#1fa8f0' : '#c8d4de',
                                },
                              }}
                            >
                              {opt}
                            </Button>
                          )
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ) : null}

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              variant="text"
              color="inherit"
              onClick={openBackModal}
              data-testid="goal-analysis-back"
              sx={{ color: '#4a5565', textTransform: 'none' }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={onProposeLoi}
              disabled={completeStep2.isPending}
              data-testid="goal-analysis-propose-loi"
            >
              Propose Lines of Inquiry
            </Button>
          </Stack>
        </Stack>
      </CampaignWizardChrome>

      <ConfirmCancelModal
        open={backOpen}
        iconVariant="caution"
        headline="Leave AI review?"
        bodyLines={[
          'If you go back and change your goal, the AI analysis will be reset.',
          'Your previous answers will be cleared.',
        ]}
        stayLabel="Cancel"
        confirmLabel="Yes, Go Back"
        onStay={cancelBack}
        onConfirm={confirmGoBack}
        dialogTestId="back-warning-dialog"
        confirmTestId="back-warning-confirm"
      />

      <ConfirmPositiveModal
        open={editGoalOpen}
        iconVariant="info"
        headline="Edit Research Goal?"
        bodyLines={[
          'If you go back and change your goal, the AI analysis will be reset.',
          'Your previous answers will be cleared.',
        ]}
        dismissLabel="Cancel"
        confirmLabel="Yes, Edit Goal"
        onDismiss={cancelEditGoal}
        onConfirm={confirmEditGoal}
        dialogTestId="edit-research-goal-dialog"
        dismissTestId="edit-research-goal-cancel"
        confirmTestId="edit-research-goal-confirm"
      />

      <CampaignGeneratingDialog
        open={loiOpen}
        subtitle="Generating Lines of Inquiry"
        dataTestId="generating-loi-dialog"
      />
    </>
  )
}
